import type { ClaimsBillingInfo } from '@monolog/shared/types';
import type { Billing } from '@database/types';
import { map, type Observable } from 'rxjs';

import GatewayBase from './gateway-base';

export enum CurrentInvoice {
  ID = 'current',
  STATUS = 'Current Cycle',
}

// Both list branches in billing-svc select the same subset, so the wire shape is narrower than
// Billing.Invoice.
export type InvoiceListItem = Pick<
  Billing.Invoice,
  | 'id'
  | 'chargeAttempts'
  | 'startDate'
  | 'endDate'
  | 'status'
  | 'storeId'
  | 'totalAmount'
  | 'totalCanceled'
  | 'totalCorrection'
  | 'baseAmount'
  | 'usageAmount'
  | 'finalRevenue'
  | 'shippingAmount'
  | 'warrantyAmount'
  | 'payoutAmount'
  | 'revenueShareAmount'
  | 'resolutionAmount'
  | 'outerMonthResolutionAmount'
  | 'includedInShippingAmount'
>;

// The current cycle is summed on the fly by invoices.service.ts getCurrentCycleSum rather than read
// from a row: no persisted id, and a status that isn't part of the InvoiceStatus enum.
export type CurrentCycleInvoice = Pick<
  InvoiceListItem,
  | 'startDate'
  | 'endDate'
  | 'finalRevenue'
  | 'chargeAttempts'
  | 'storeId'
  | 'baseAmount'
  | 'usageAmount'
  | 'totalCorrection'
  | 'payoutAmount'
  | 'revenueShareAmount'
  | 'resolutionAmount'
  | 'outerMonthResolutionAmount'
  | 'includedInShippingAmount'
> & { id: CurrentInvoice.ID; status: CurrentInvoice.STATUS };

type PlanUpdateInput = {
  baseFee?: number;
  shippingRevSharePercentage: number;
  extendedWarrantyRevSharePercentage: number | null;
  invoiceCycle: string;
  invoiceModel?: string;
  payoutModel?: string;
  marginThreshold?: number | null;
  marginThresholdType?: Billing.MarginThresholdType | null;
  billingType?: Billing.BillingType;
  billingModel?: Billing.BillingModel | null;
  excludeDiscounts?: boolean;
};

export type PartnerPlan = {
  id: string;
  partnerAccountId: string;
  storeId: string;
  partnerPercentage: number;
  createdAt: string;
  voidedAt: string | null;
};

export type PartnerPlanSummary = {
  partnerAccountId: string | null;
  active: (PartnerPlan & { totalPayoutPercentage: number | null }) | null;
  history: PartnerPlan[];
};

export type PartnerPayoutRow = Billing.PartnerPayout & {
  storeName: string;
  // One entry per partner plan version live during the cycle, oldest first.
  partnerPercentages: number[];
  invoice: Pick<Billing.Invoice, 'id' | 'status'> | null;
};

export type PartnerPayoutMonthRun = {
  payouts: Billing.PartnerPayout[];
  created: number;
  existing: number;
  skipped: number;
};

// Partner-facing payout row: no invoice link.
export type PartnerPayoutHistoryRow = Pick<
  Billing.PartnerPayout,
  | 'id'
  | 'storeId'
  | 'startDate'
  | 'endDate'
  | 'premiumAmount'
  | 'discountAmount'
  | 'canceledAmount'
  | 'partnerShare'
  | 'status'
  | 'updatedAt'
> & {
  storeName: string;
  // One entry per partner plan version live during the cycle, oldest first.
  partnerPercentages: number[];
};

export default class BilingRestService extends GatewayBase {
  get partner() {
    return {
      plans: this.getOwnPartnerPlans,
      payouts: this.getOwnPartnerPayouts,
    };
  }

  get partnerPayouts() {
    return {
      get: this.getPartnerPayouts,
      markPaid: this.markPartnerPayoutsPaid,
      partners: this.getPayoutPartners,
    };
  }

  get partnerPlan() {
    return {
      get: this.getPartnerPlan,
      save: this.savePartnerPlan,
      void: this.voidPartnerPlan,
    };
  }

  get plans() {
    return {
      find: this.getActivePlan,
      get: this.getPlans,
      update: this.updatePlan,
    };
  }

  get invoices() {
    return {
      admin: {
        charge: this.chargeInvoices,
        update: this.updateInvoices,
      },
      billCount: this.getBillCount,
      bills: this.getInvoiceBills,
      current: this.getCurrentInvoice,
      discountCount: this.getInvoiceDiscountCount,
      discounts: this.getInvoiceDiscounts,
      export: this.exportInvoice,
      exportDiscounts: this.exportInvoiceDiscounts,
      exportInvoices: this.exportInvoices,
      exportPriorMonths: this.exportInvoicePriorMonths,
      exportResolutions: this.exportInvoiceResolutions,
      exportRevenueShare: this.exportInvoiceRevenueShare,
      get: this.getInvoices,
      priorMonths: this.getInvoicePriorMonths,
      resolutions: this.getInvoiceResolutions,
    };
  }

  get payouts() {
    return {
      admin: {
        get: this.getAdminPayouts,
        update: this.updatePayout,
      },
      bills: this.getPayoutBills,
      byId: this.getPayout,
      byResolution: this.getPayoutByResolution,
      corrections: this.getPayoutCorrections,
      counts: this.getPayoutCounts,
      credited: this.getPayoutCredited,
      current: this.getCurrentPayout,
      discounts: this.getPayoutDiscounts,
      export: {
        corrections: this.exportCorrections,
        credited: this.exportCreditedResolutions,
        discounts: this.exportDiscounts,
        payouts: this.exportPayouts,
        priorMonths: this.exportPriorMonths,
        revenueShare: this.exportRevenueShare,
        uncredited: this.exportUncreditedResolutions,
      },
      get: this.getPayouts,
      iframe: this.getIFrame,
      listByIds: this.getPayoutList,
      notCredited: this.getPayoutNotCredited,
      payee: this.getPayee,
      priorMonth: this.getPayoutPriorMonths,
    };
  }

  get corrections() {
    return {
      used: this.getPayoutCorrections,
    };
  }

  get billingMethods() {
    return {
      batchCreate: this.batchCreateBillingMethods,
      create: this.createBillingMethod,
      delete: this.deleteBillingMethod,
      get: this.getBillingMethods,
      uniqueByMerchant: this.getUniqueMethods,
      update: this.updateBillingMethod,
    };
  }

  get billingInformation() {
    return {
      get: this.getBillingInformation,
    };
  }

  get settings() {
    return {
      byStore: this.getBillingSettings,
      update: this.updateBillingSettings,
    };
  }

  get jobs() {
    return {
      createPartnerPayout: this.createPartnerPayout,
      createPartnerPayoutsForMonth: this.createPartnerPayoutsForMonth,
      runInvoices: this.runInvoicesJob,
      runPayouts: this.runPayoutsJob,
    };
  }

  private runInvoicesJob = (body: {
    storeIds: string[];
    startDate: string;
    endDate: string;
    shouldCharge: boolean;
  }) => {
    return this.client.post('v1/billing/jobs/invoices', body);
  };

  private runPayoutsJob = (body: { storeIds: string[]; startDate: string; endDate: string }) => {
    return this.client.post('v1/billing/jobs/payouts', body);
  };

  private createPartnerPayout = (invoiceId: string) => {
    return this.client.post<Billing.PartnerPayout>('v1/billing/jobs/partner-payouts', { invoiceId });
  };

  private createPartnerPayoutsForMonth = (body: { partnerAccountId: string; month: string }) => {
    return this.client.post<PartnerPayoutMonthRun>('v1/billing/jobs/partner-payouts/month', body);
  };

  private getActivePlan = (storeId: string) => {
    return this.client.get<Billing.Plan>(`v1/billing/${storeId}/plans/active`);
  };

  private getPlans = (storeId: string) => {
    return this.client.get<Billing.Plan[]>(`v1/billing/${storeId}/plans`);
  };

  // The invoice routes return the bare payload; most other billing routes wrap with
  // ResponseInterceptor as { data: ... }. Unwrap either shape so callers see one type.
  private getInvoices = (
    storeId: string,
    params?: { startDate?: string; endDate?: string },
  ): Observable<InvoiceListItem[]> => {
    return this.client
      .get<InvoiceListItem[]>(`v1/billing/${storeId}/invoices`, { params })
      .pipe(map(res => (res?.data ?? res) as InvoiceListItem[]));
  };

  private getPayoutBills = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number; policyType?: string },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/bills`, { params });
  };

  private getInvoiceBills = (storeId: string, invoiceId: string, params: { page?: number; limit?: number }) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/bills`, { params });
  };

  private getInvoiceDiscounts = (
    storeId: string,
    invoiceId: string,
    params: { page?: number; limit?: number; startDate?: string; endDate?: string },
  ) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/discounts`, { params });
  };

  private getInvoiceDiscountCount = (
    storeId: string,
    invoiceId: string,
    params: { startDate?: string; endDate?: string },
  ) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/discounts/count`, { params });
  };

  private exportInvoiceDiscounts = (storeId: string, invoiceId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/invoices/${invoiceId}/discounts/export`, body);
  };

  private getInvoiceResolutions = (
    storeId: string,
    invoiceId: string,
    params: { page?: number; limit?: number; startDate?: string; endDate?: string },
  ) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/resolutions`, { params });
  };

  private getInvoicePriorMonths = (
    storeId: string,
    invoiceId: string,
    params: { page?: number; limit?: number; startDate?: string; endDate?: string },
  ) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/prior-months`, { params });
  };

  private getPayoutCounts = (storeId: string, payoutId: string, params: { startDate?: Date; endDate?: Date }) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/counts`, { params });
  };

  private getBillCount = (storeId: string, invoiceId: string, params: { startDate?: Date; endDate?: Date }) => {
    return this.client.get(`v1/billing/${storeId}/invoices/${invoiceId}/bills/count`, { params });
  };

  private updateInvoices = (storeId: string, input) => {
    return this.client.put(`v1/billing/${storeId}/invoices/`, input);
  };

  private chargeInvoices = (storeId: string, input) => {
    return this.client.post(`v1/billing/${storeId}/invoices/charge`, input);
  };

  private getPayoutCredited = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/credited`, { params });
  };

  private getPayoutNotCredited = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/not-credited`, { params });
  };

  private getPayoutDiscounts = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/discounts`, { params });
  };

  private getPayoutPriorMonths = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/prior-months`, { params });
  };

  private getPayoutCorrections = (
    storeId: string,
    payoutId: string,
    params: { startDate: Date; endDate: Date; page?: number; limit?: number },
  ) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}/corrections`, { params });
  };

  private getAdminPayouts = (storeId: string, usedId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/admin`);
  };

  private updatePayout = (storeId: string, payoutId: string, status: string) => {
    return this.client.put(`v1/billing/${storeId}/payouts/${payoutId}`, status);
  };

  private getPayouts = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts`);
  };

  private getPayout = (storeId: string, payoutId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/${payoutId}`);
  };

  private getPayoutByResolution = (storeId: string, resolutionId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/resolutions/${resolutionId}`);
  };

  private getPayoutList = (storeId: string, payoutIds: string[]) => {
    return this.client.get(`v1/billing/${storeId}/payouts/list`, { params: { payoutIds: payoutIds.join(',') } });
  };

  private getCurrentPayout = (storeId: string, params?: { startDate?: Date; endDate?: Date; invoiceId?: string }) => {
    return this.client.get(`v1/billing/${storeId}/payouts/current`, { params });
  };

  private getIFrame = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/iframe`);
  };

  private getPayee = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/payouts/payee`);
  };

  private getBillingMethods = (storeId: string) => {
    return this.client.get(`v1/billing/${storeId}/billing-methods`);
  };

  private createBillingMethod = (storeId: string, input) => {
    return this.client.post(`v1/billing/${storeId}/billing-methods`, input);
  };

  private getUniqueMethods = (storeId: string, accountId: string) => {
    return this.client.get(`v1/billing/${storeId}/billing-methods/merchant/${accountId}/unique`);
  };

  private batchCreateBillingMethods = (storeId: string, input) => {
    return this.client.post(`v1/billing/${storeId}/billing-methods/batch`, input);
  };

  private updateBillingMethod = (storeId: string, billingMethodId: string) => {
    return this.client.put(`v1/billing/${storeId}/billing-methods/${billingMethodId}`);
  };

  private deleteBillingMethod = (storeId: string, billingMethodId: string) => {
    return this.client.delete(`v1/billing/${storeId}/billing-methods/${billingMethodId}`);
  };

  private getCurrentInvoice = (storeId: string): Observable<CurrentCycleInvoice> => {
    return this.client
      .get<CurrentCycleInvoice>(`v1/billing/${storeId}/invoices/current`)
      .pipe(map(res => (res?.data ?? res) as CurrentCycleInvoice));
  };

  private updatePlan = (storeId: string, input: PlanUpdateInput) => {
    return this.client.put<Billing.Plan>(`v1/billing/${storeId}/plans`, input);
  };

  private getPayoutPartners = () => {
    return this.client.get<Array<{ partnerAccountId: string; name: string }>>('v1/billing/partner-payouts/partners');
  };

  private getOwnPartnerPlans = () => {
    return this.client.get<Array<{ storeId: string; partnerPercentage: number }>>('v1/billing/partner/partner-plans');
  };

  private getOwnPartnerPayouts = () => {
    return this.client.get<PartnerPayoutHistoryRow[]>('v1/billing/partner/payout-history');
  };

  private getPartnerPayouts = (partnerAccountId: string) => {
    return this.client.get<PartnerPayoutRow[]>('v1/billing/partner-payouts', { params: { partnerAccountId } });
  };

  // Marks a whole month (several brands) or a single brand paid in one call.
  private markPartnerPayoutsPaid = (partnerAccountId: string, partnerPayoutIds: string[]) => {
    return this.client.post<{ paid: number }>('v1/billing/partner-payouts/mark-paid', {
      partnerAccountId,
      partnerPayoutIds,
    });
  };

  private getPartnerPlan = (storeId: string) => {
    return this.client.get<PartnerPlanSummary>(`v1/billing/${storeId}/partner-plan`);
  };

  private savePartnerPlan = (storeId: string, partnerPercentage: number) => {
    return this.client.put<PartnerPlan>(`v1/billing/${storeId}/partner-plan`, { partnerPercentage });
  };

  private voidPartnerPlan = (storeId: string) => {
    return this.client.delete<void>(`v1/billing/${storeId}/partner-plan`);
  };

  private getBillingSettings = (storeId: string) => {
    return this.client.get<{ exemptedEmails: string; id: string }>('v1/billing/settings', { params: { storeId } });
  };

  private updateBillingSettings = (id: string, updates: { exemptedEmails: string }) => {
    return this.client.put<{ exemptedEmails: string }>(`v1/billing/settings/${id}`, updates);
  };

  private exportInvoice = (storeId: string, invoiceId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/invoices/${invoiceId}/export`, body);
  };

  private exportInvoiceRevenueShare = (storeId: string, invoiceId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/invoices/${invoiceId}/revenue-share/export`, body);
  };

  private exportInvoicePriorMonths = (storeId: string, invoiceId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/invoices/${invoiceId}/prior-months/export`, body);
  };

  private exportInvoiceResolutions = (storeId: string, invoiceId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/invoices/${invoiceId}/resolutions/export`, body);
  };

  private exportRevenueShare = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/revenue-share/export`, body);
  };

  private exportDiscounts = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/discounts/export`, body);
  };

  private exportPriorMonths = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/prior-months/export`, body);
  };

  private exportCorrections = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/corrections/export`, body);
  };

  private exportCreditedResolutions = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/credited/export`, body);
  };

  private exportUncreditedResolutions = (storeId: string, payoutId: string, body: unknown) => {
    return this.client.post(`v1/billing/${storeId}/payouts/${payoutId}/not-credited/export`, body);
  };

  private exportPayouts = (storeId: string, body: { startDate?: string; endDate?: string }) => {
    return this.client.post(`v1/billing/${storeId}/payouts/export`, body);
  };

  private exportInvoices = (
    storeId: string,
    body: { startDate?: string; endDate?: string; grain?: 'summary' | 'order' },
  ) => {
    return this.client.post(`v1/billing/${storeId}/invoices/export`, body);
  };

  private getBillingInformation = (storeId: string) => {
    return this.client.get<ClaimsBillingInfo>(`v1/billing-information/${storeId}`);
  };
}
